import { fromEvent, take, tap } from 'rxjs';
import { createWebMDemuxer, Track, WebMDemuxer } from 'webm-inspector';
import { EbmlElement } from 'webm-inspector/lib/demuxer/webm/ebml/ebml-types';
import { ArrayBufferTarget, Muxer } from 'webm-muxer';

interface VideoTrack extends Track {
  codec: string;
  metadata: { PixelWidth: number; PixelHeight: number };
}

interface AudioTrack extends Track {
  codec: string;
  metadata: { SamplingFrequency: number; Channels: number; BitDepth: number };
}

export function createRecord(
  tracks: MediaStreamTrack[],
  callback: (blob: Blob, mimetype: string) => void | Promise<void>,
): MediaRecorder | null {
  const tmpMediaStreams = tracks
    .filter((i): i is NonNullable<typeof i> => !!i)
    .map((item) => {
      const mediaStream2 = new MediaStream([item]);
      item.addEventListener('ended', () => {
        mediaStream2.getTracks().forEach((item2) => item2.stop());
      });
      return new MediaStream([item]);
    });

  const mediaStreams = [
    ...tmpMediaStreams.filter((item) => item.getVideoTracks().length > 0),
    ...tmpMediaStreams.filter((item) => item.getAudioTracks().length > 0),
  ];

  const mediaStream = mediaStreams[0];
  let recordedChunks: Blob[] = [];
  const szWebM: Blob[] = [];

  const done = async (e: BlobEvent) => {
    let maxTimestamp = -1;
    szWebM.push(e.data);
    if (szWebM.length == mediaStreams.length) {
      const { blob: blobWebM, maxTimestamp: _maxTimestamp } = await combinaWebM(szWebM[0], szWebM[1]);
      recordedChunks.push(blobWebM);
      if (_maxTimestamp > maxTimestamp) {
        maxTimestamp = _maxTimestamp;
      }

      const mimetype = 'video/webm';
      const blob = new Blob(recordedChunks, {
        type: mimetype,
      });

      await Promise.resolve(callback(blob, mimetype));

      recordedChunks = [];
    }
  };

  const mediaRecorder = createMediaRecorder(mediaStream, done);

  if (mediaStreams.length > 1) {
    createMediaRecorder(mediaStreams[1], done, mediaRecorder);
  }

  return mediaRecorder;
}

export function createMediaRecorder(
  mediaStream: MediaStream,
  done: (event: BlobEvent) => void,
  mediaRecorderPartner?: MediaRecorder,
): MediaRecorder {
  const hasVideo = mediaStream.getVideoTracks().length > 0;
  const hasAudio = mediaStream.getAudioTracks().length > 0;
  const options =
    hasAudio && hasVideo
      ? { mimeType: 'video/webm; codecs=vp8,opus' }
      : hasVideo
        ? { mimeType: 'video/webm; codecs=vp8' }
        : { mimeType: 'audio/webm; codecs=opus' };

  const mediaRecorder = new MediaRecorder(mediaStream, options);

  fromEvent<BlobEvent>(mediaRecorder, 'dataavailable')
    .pipe(
      take(1),
      tap((event) => done(event)),
    )
    .subscribe();

  if (mediaRecorderPartner) {
    fromEvent(mediaRecorderPartner, 'start')
      .pipe(
        take(1),
        tap(() => mediaRecorder.start()),
      )
      .subscribe();

    fromEvent<BlobEvent>(mediaRecorderPartner, 'dataavailable')
      .pipe(
        take(1),
        tap(() => {
          if (mediaRecorder.state === 'recording') {
            mediaRecorder.requestData();
          }
        }),
      )
      .subscribe();
  }

  return mediaRecorder;
}

async function combinaWebM(webM1: Blob, webM2: Blob): Promise<{ blob: Blob; maxTimestamp: number }> {
  const stream0 = webM1 ? await getDemuxer(webM1) : null;
  const stream1 = webM2 ? await getDemuxer(webM2) : null;
  const audio =
    stream0?.tracks[1].type === 'audio' ? stream0 : stream1?.tracks[1].type === 'audio' ? stream1 : undefined;
  const video =
    stream0?.tracks[1].type === 'video' ? stream0 : stream1?.tracks[1].type === 'video' ? stream1 : undefined;
  const videoTrackInfo = video?.tracks[1] as VideoTrack | undefined;
  const audioTrackInfo = audio?.tracks[1] as AudioTrack | undefined;

  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    type: 'webm',
    streaming: false,
    ...(video && videoTrackInfo
      ? {
          video: {
            codec: `V_${videoTrackInfo.codec}`,
            width: videoTrackInfo.metadata.PixelWidth,
            height: videoTrackInfo.metadata.PixelHeight,
          },
        }
      : {}),
    ...(audio && audioTrackInfo
      ? {
          audio: {
            codec: `A_${audioTrackInfo.codec}`,
            sampleRate: audioTrackInfo.metadata.SamplingFrequency,
            numberOfChannels: audioTrackInfo.metadata.Channels,
            bitDepth: audioTrackInfo.metadata.BitDepth,
          },
        }
      : {}),
  });

  const TickHandleFrame = ({
    audioIDemuxer,
    videoIDemuxer,
  }: {
    audioIDemuxer?: WebMDemuxer;
    videoIDemuxer?: WebMDemuxer;
  }) => {
    const parser = new SimpleBlockParser();

    const GetMediaElements = (
      mediaType: string,
      trackId: number,
      iIDemuxer?: WebMDemuxer,
    ): [number, EbmlElement, boolean][] => {
      const elements = iIDemuxer
        ? iIDemuxer.elements.filter((item) => item.type == 'b' || item.name == 'BlockGroup')
        : [];

      return elements.map((item) => [parser.getTimestamp(item, trackId), item, mediaType == 'video']);
    };

    const audioDataElemets = GetMediaElements('audio', 1, audioIDemuxer);
    const videoDataElemets = GetMediaElements('video', 0, videoIDemuxer);
    const mediaDataElements = audioDataElemets.concat(videoDataElemets).sort((a, b) => a[0] - b[0]);
    const callback = (
      handler: (t: number, data: Uint8Array<ArrayBufferLike>, bVideo: boolean, bKeyFrame: boolean) => void,
    ) => {
      mediaDataElements.forEach((element) => {
        const [timestamp, ele, bVideo] = element;
        const [data, bKeyFrame] = parser.getData(ele);
        handler(timestamp, data, bVideo, bKeyFrame);
      });
    };
    return callback;
  };
  const handleChunk = TickHandleFrame({
    audioIDemuxer: audio,
    videoIDemuxer: video,
  });

  const audioCodecPrivate = audio?.elements
    .find((i) => i.name === 'Tracks')
    ?.childs[0].childs.find((item) => item.name === 'CodecPrivate')?.data as Uint8Array<ArrayBufferLike> | undefined;

  const config: EncodedAudioChunkMetadata | undefined = audio
    ? {
        decoderConfig: {
          sampleRate: audioTrackInfo?.metadata?.SamplingFrequency as number,
          numberOfChannels: audioTrackInfo?.metadata?.Channels as number,
          description: audioCodecPrivate!,
          codec: audioTrackInfo?.codec as string,
        },
      }
    : undefined;

  let maxTimestamp = -1;

  handleChunk((timestamp, data, bVideo, bKeyFrame) => {
    if (bVideo) {
      muxer.addVideoChunkRaw(data, bKeyFrame ? 'key' : 'delta', timestamp);
    } else {
      if (config) {
        muxer.addAudioChunkRaw(data, 'key', timestamp, config);
      }
    }
    if (maxTimestamp < timestamp) {
      maxTimestamp = timestamp;
    }
  });

  muxer.finalize();
  return {
    blob: new Blob([muxer.target.buffer]),
    maxTimestamp,
  };
}

async function getDemuxer(data: Blob): Promise<WebMDemuxer> {
  const webMData = new Uint8Array(await data.arrayBuffer());
  const demuxer = createWebMDemuxer();
  demuxer.append(webMData);
  demuxer.end();
  return demuxer;
}

class SimpleBlockParser {
  protected trackTimestampMap: Record<number, number> = {};
  protected trackTimestampMapMins: Record<number, number> = {};

  getTimestamp(simpleBlock: EbmlElement, trackId: number): number {
    if (simpleBlock.name == 'BlockGroup') {
      const tempBlock = simpleBlock.childs.find((item) => item.type == 'b');
      if (tempBlock) {
        simpleBlock = tempBlock;
      }
    }
    if (typeof this.trackTimestampMap[trackId] == 'undefined') {
      this.trackTimestampMap[trackId] = 0;
    }
    if (typeof this.trackTimestampMapMins[trackId] == 'undefined') {
      this.trackTimestampMapMins[trackId] = 0;
    }
    const lastTime = this.trackTimestampMap[trackId];
    const lastTimeMins = this.trackTimestampMapMins[trackId];
    const data = simpleBlock.data as Uint8Array<ArrayBufferLike>;
    const blockTime = data[1] * 256 + data[2];
    const baseTime = blockTime < lastTime ? lastTime + (blockTime == 0 ? 0 : lastTimeMins) : 0;
    if (blockTime < lastTime) {
      this.trackTimestampMapMins[trackId] = -1 * blockTime;
    }
    const currentTime = baseTime + blockTime;
    this.trackTimestampMap[trackId] = currentTime;
    return currentTime * 1e3;
  }

  getData(simpleBlock: EbmlElement): [Uint8Array<ArrayBufferLike>, boolean] {
    if (simpleBlock.name == 'BlockGroup') {
      const tempBlock = simpleBlock.childs.find((item) => item.type == 'b');
      if (tempBlock) {
        simpleBlock = tempBlock;
      }
    }
    const data = simpleBlock.data as Uint8Array<ArrayBufferLike>;
    const blockTime = data[1] * 256 + data[2];
    return [data.slice(4), blockTime == 0];
  }

  parse(simpleBlock: EbmlElement, trackId: number): [number, Uint8Array<ArrayBufferLike>, boolean] {
    if (simpleBlock.name == 'BlockGroup') {
      const tempBlock = simpleBlock.childs.find((item) => item.type == 'b');
      if (tempBlock) {
        simpleBlock = tempBlock;
      }
    }
    if (typeof this.trackTimestampMap[trackId] == 'undefined') {
      this.trackTimestampMap[trackId] = 0;
    }
    if (typeof this.trackTimestampMapMins[trackId] == 'undefined') {
      this.trackTimestampMapMins[trackId] = 0;
    }
    const lastTime = this.trackTimestampMap[trackId];
    const lastTimeMins = this.trackTimestampMapMins[trackId];
    const data = simpleBlock.data as Uint8Array<ArrayBufferLike>;
    const blockTime = data[1] * 256 + data[2];
    const baseTime = blockTime < lastTime ? lastTime + (blockTime == 0 ? 0 : lastTimeMins) : 0;
    const bKeyFrame = blockTime == 0;
    if (blockTime < lastTime) {
      this.trackTimestampMapMins[trackId] = -1 * blockTime;
    }
    const currentTime = baseTime + blockTime;
    this.trackTimestampMap[trackId] = currentTime;
    return [currentTime * 1e3, data.slice(4), bKeyFrame];
  }
}
